import { useCallback } from 'react';
import { useExperiments } from '@wix/fe-essentials-standalone';
import { SHOW_MEANINGFUL_ERROR_MSG } from '@src/utils/constants';
import { ACTIONS, useAppContext } from '../Contexts/AppContext';
import createFeedbackAPI from '@src/utils/FeedbackAPI';

import * as Sentry from '@sentry/browser';

const useFeedbackApi = () => {
  const {
    dispatch,
    state: {
      editorType,
      feedbackCreatorUserGuid,
      feedbackId,
      metaSiteId,
      ownerId,
      pMetaSiteId,
      siteId,
      siteName,
      linkKey,
      instance,
    },
  } = useAppContext();
  const { experiments } = useExperiments({ readOnly: true });

  const getErrorKeyByShowMeaningfulErrorMsgExperiment = (key: string) => {
    if (experiments.enabled(SHOW_MEANINGFUL_ERROR_MSG)) {
      return key;
    }
    return 'get_feedback_error';
  };

  const feedbackAPI = createFeedbackAPI({
    editorType,
    feedbackCreatorUserGuid,
    feedbackId,
    metaSiteId,
    ownerId,
    pMetaSiteId,
    siteId,
    siteName,
    linkKey,
    instance,
  });

  const getInitializeData = useCallback(async () => {
    try {
      return await feedbackAPI.getInitializeData();
    } catch (e) {
      Sentry.captureException(e);
      dispatch({
        type: ACTIONS.SET_ERROR,
        payload: {
          showError: true,
          errorMsgLocalKey: 'get_feedback_error',
        },
      });
      return undefined;
    }
  }, [feedbackAPI]);

  const addComment = useCallback(
    async ({ message, commentLocation, status, shouldSchedule, userInfo }) => {
      try {
        return await feedbackAPI.addComment({
          message,
          commentLocation,
          status,
          shouldSchedule,
          userInfo,
        });
      } catch (e) {
        Sentry.captureException(e);
        dispatch({
          type: ACTIONS.SET_ERROR,
          payload: {
            showError: true,
            errorMsgLocalKey: getErrorKeyByShowMeaningfulErrorMsgExperiment(
              'get_feedback_add_comment_error',
            ),
          },
        });
        return undefined;
      }
    },
    [feedbackAPI, experiments],
  );

  const createContact = useCallback(
    async ({ userName, userEmail }) => {
      try {
        return await feedbackAPI.createContact({ userName, userEmail });
      } catch (e) {
        Sentry.captureException(e, {
          extra: {
            userName,
          },
        });
        dispatch({
          type: ACTIONS.SET_ERROR,
          payload: {
            showError: true,
            errorMsgLocalKey: getErrorKeyByShowMeaningfulErrorMsgExperiment(
              'get_feedback_create_contact_error',
            ),
          },
        });
        return { contact: { contactId: null } };
      }
    },
    [feedbackAPI, experiments],
  );

  const updateComment = useCallback(
    async ({
      id,
      message,
      commentLocation,
      commenterId,
      status,
      revision,
      fields,
    }: {
      id: string;
      message: string;
      commentLocation: any;
      commenterId: string;
      status: string;
      revision: string;
      fields: any;
    }) => {
      try {
        return await feedbackAPI.updateComment({
          id,
          message,
          commentLocation,
          commenterId,
          status,
          revision,
          fields,
          feedbackId,
        });
      } catch (e) {
        Sentry.captureException(e);
        dispatch({
          type: ACTIONS.SET_ERROR,
          payload: {
            showError: true,
            errorMsgLocalKey: getErrorKeyByShowMeaningfulErrorMsgExperiment(
              'get_feedback_edit_comment_error',
            ),
          },
        });
      }
    },
    [feedbackAPI, feedbackId, experiments],
  );

  const deleteComment = useCallback(
    async (commentId: string) => {
      try {
        await feedbackAPI.deleteComment(commentId);
        return true;
      } catch (e) {
        Sentry.captureException(e);
        dispatch({
          type: ACTIONS.SET_ERROR,
          payload: {
            showError: true,
            errorMsgLocalKey: getErrorKeyByShowMeaningfulErrorMsgExperiment(
              'get_feedback_delete_comment_error',
            ),
          },
        });
      }

      return false;
    },
    [feedbackAPI, experiments],
  );

  const replyToComment = useCallback(
    async ({
      id,
      revision,
      replyText,
      userInfo,
      commenterId,
    }: {
      id: string;
      revision: string;
      replyText: string;
      userInfo: any;
      commenterId: string;
      commentLocation: any;
      status: string;
    }) => {
      try {
        return await feedbackAPI.replyToComment({
          id,
          revision,
          replyText,
          userInfo,
          commenterId,
          isOwner: false,
        });
      } catch (e) {
        Sentry.captureException(e);
        dispatch({
          type: ACTIONS.SET_ERROR,
          payload: {
            showError: true,
            errorMsgLocalKey: getErrorKeyByShowMeaningfulErrorMsgExperiment(
              'get_feedback_add_reply_error',
            ),
          },
        });
        return undefined;
      }
    },
    [feedbackAPI, experiments],
  );

  return {
    addComment,
    createContact,
    updateComment,
    deleteComment,
    replyToComment,
    getInitializeData,
  };
};

export default useFeedbackApi;
